/**
 * DeviceDetailPanel — P0-4 (Etapa 3 da Sprint Fleet Explorer Finalização).
 *
 * Painel direito operacional, SOMENTE LEITURA. Substitui o placeholder
 * da Etapa 1. Renderiza a partir do `device` (DTO do mapa) e enriquece
 * Watchdog runtime + Agenda real via getDeviceConfigSnapshot (API já
 * existente, read-only). NENHUMA ação de escrita: sem relé, sem agenda,
 * sem watchdog, sem comandos. Os botões são apenas de navegação.
 *
 * Estratégia defensiva: o painel nunca bloqueia no fetch do snapshot —
 * cabeçalho/cliente/relé/telemetria vêm do DTO. Watchdog/Agenda mostram
 * "carregando…" e depois o dado ou "não disponível". Campos nulos viram
 * "não disponível" (Row) ou somem (seções/listas opcionais).
 */
import { useEffect, useState } from "react";
import { getDeviceConfigSnapshot } from "../../lib/api.js";
import { resolveActiveSchedule, fmtScheduleEpoch } from "../../lib/scheduleCore.js";
import DeviceCommands from "./DeviceCommands.jsx";
import DeviceDiagnostics from "./DeviceDiagnostics.jsx";
import ScheduleV2Card from "./ScheduleV2Card.jsx";
import { getProtectionState } from "../../lib/protection.js";

const _DOW = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function fmtAgo(iso) {
  if (!iso) return "—";
  try {
    const ts = new Date(iso);
    const diff = Math.round((Date.now() - ts.getTime()) / 1000);
    if (diff < 60) return `${diff}s atrás`;
    if (diff < 3600) return `${Math.round(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h atrás`;
    return `${Math.round(diff / 86400)}d atrás`;
  } catch {
    return "—";
  }
}

// Valor "vazio" defensivo: null/undefined/"" → null (renderiza "não disponível").
function val(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  return v;
}

function fmtDateTime(iso) {
  const v = val(iso);
  if (!v) return null;
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return null;
  }
}

function scoreColor(score) {
  if (score == null) return "#7e8aa0";
  if (score >= 80) return "#35E8A4";
  if (score >= 50) return "#FFB224";
  return "#f87171";
}

// Linha label/valor. value null → "não disponível".
function Row({ label, value, mono = false, highlight = null }) {
  const has = value !== null && value !== undefined && value !== "";
  return (
    <div className="ipwd-rp-row">
      <span className="ipwd-rp-row-label">{label}</span>
      {has ? (
        <span
          className={"ipwd-rp-row-val" + (mono ? " is-mono" : "")}
          style={highlight ? { color: highlight } : undefined}
        >
          {value}
        </span>
      ) : (
        <span className="ipwd-rp-na">não disponível</span>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="ipwd-rp-section">
      <div className="ipwd-rp-section-title">{title}</div>
      {children}
    </div>
  );
}

export default function DeviceDetailPanel({ device, onClose, onOpenDevice, onCommand }) {
  const [snap, setSnap] = useState(null);
  const [snapState, setSnapState] = useState("idle"); // idle | loading | ok | error
  const [snapSyncedAt, setSnapSyncedAt] = useState(null); // idade do snapshot

  const deviceId = device?.device_id || null;

  useEffect(() => {
    if (!deviceId) return;
    let alive = true;
    const ctrl = new AbortController();
    setSnap(null);
    setSnapState("loading");
    getDeviceConfigSnapshot(deviceId, { signal: ctrl.signal })
      .then((data) => {
        if (!alive) return;
        setSnap(data?.last_config_snapshot || null);
        setSnapSyncedAt(data?.config_synced_at || null);
        setSnapState("ok");
      })
      .catch(() => {
        if (!alive) return;
        setSnapState("error");
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [deviceId]);

  if (!device) return null;

  const online = !!device.online;
  const relayOff = device.relay === "off";
  const score = device.health_score;

  // MAP-HEALTH-1B — "último boot" aproximado (now − uptime_s). O "~" deixa
  // claro que é estimativa (uptime desde o último boot, não um reboot exato).
  const lastBootAprox = (device.uptime_s != null && device.uptime_s >= 0)
    ? `~${fmtAgo(new Date(Date.now() - device.uptime_s * 1000).toISOString())}`
    : null;

  // MAP-UX-1C — derivados de status p/ as seções Firmware/Segurança/Proteção
  // (render-puro do DTO MAP-DATA-1A). fw fora do alvo: fw_version ≠ release alvo.
  const fwStale = !!(
    device.fw_version && device.target_release_version &&
    device.fw_version !== device.target_release_version
  );
  // HMAC inseguro = secure_channel=false E secret definido (não conta device
  // novo pré-bootstrap, que tem auth_secret_set=false).
  const hmacInsecure = device.secure_channel === false && device.auth_secret_set === true;
  const anchorBad = !!(device.mac_anchor_status && device.mac_anchor_status !== "ok");
  const cloneSuspect = device.clone_suspected === true;
  const protectedDev = getProtectionState(device).protected;
  const authPending = !!(device.authorization_status && device.authorization_status !== "authorized");
  const hasSecurityInfo = (
    device.secure_channel != null || device.auth_secret_set != null ||
    device.mac != null || device.mac_anchor_status != null || cloneSuspect
  );

  // INA226-2 — visor de Energia. GATE: só renderiza p/ device compatível
  // (modelo esp32c3-ina226) — power_monitor_supported===true OU há dado INA226.
  // Device comum → nada disso é verdadeiro → bloco não aparece (sem vazio).
  const powerSupported = device.power_monitor_supported === true;
  const ina226Status = val(device.ina226_status);
  const hasPowerData = (
    device.bus_voltage_mv != null || device.current_ma != null ||
    device.power_mw != null || device.ina226_ok != null || ina226Status != null
  );
  const showPower = powerSupported || hasPowerData;
  const powerStatusLabel = (
    ina226Status === "ok" ? "OK"
      : ina226Status === "missing" ? "ausente"
      : ina226Status === "error" ? "erro de leitura"
      : ina226Status === "disabled" ? "desabilitado"
      : (device.ina226_ok === true ? "OK"
         : device.ina226_ok === false ? "indisponível" : val(ina226Status))
  );
  const powerStatusColor = (
    (device.ina226_ok === false || ina226Status === "error") ? "#FF5063"  // vermelho
      : ina226Status === "missing" ? "#FFB224"                            // âmbar
      : (ina226Status === "ok" || device.ina226_ok === true) ? "#35E8A4"  // verde
      : null
  );
  // mV/mA/mW → V/A/W amigável (corrente adaptativa: <1 A em mA).
  const fmtV = (mv) => (mv != null ? `${(mv / 1000).toFixed(2)} V` : null);
  const fmtA = (ma) => (ma == null ? null : (Math.abs(ma) < 1000 ? `${ma} mA` : `${(ma / 1000).toFixed(2)} A`));
  const fmtW = (mw) => (mw != null ? `${(mw / 1000).toFixed(2)} W` : null);

  // Watchdog runtime (snapshot.watchdog.*) com fallback ao DTO p/ mode/hosts.
  const wd = snap?.watchdog || null;
  const wdMode = val(wd?.mode) ?? val(device.watchdog_mode);
  const wdHost1 = val(wd?.host1) ?? val(device.watchdog_host1);
  const wdHost2 = val(wd?.host2) ?? val(device.watchdog_host2);
  const snapPending = snapState === "loading";
  const snapMissing = snapState === "error" || (snapState === "ok" && !snap);

  // S2.9 — agenda ATIVA resolvida do snapshot (v1 OU v2). Fonte = device.
  const activeSched = resolveActiveSchedule(snap, device);
  const schedEntries = activeSched.active.entries;
  const schedSource = activeSched.activeSource;

  // Localização: nome/endereço ou coordenadas.
  const coords =
    device.lat != null && device.lng != null
      ? `${Number(device.lat).toFixed(5)}, ${Number(device.lng).toFixed(5)}`
      : null;

  return (
    <aside className="ipwd-fleet-rightpanel hidden md:flex">
      {/* 1. Cabeçalho */}
      <div className="ipwd-rp-head">
        <span className="ipwd-rp-title">{device.device_id}</span>
        <button
          type="button"
          className="ipwd-rp-close"
          title="Fechar painel"
          onClick={onClose}
        >×</button>
      </div>

      <div className="ipwd-rp-status">
        <span className={online ? "is-online" : "is-offline"}>
          {online ? "● ONLINE" : "● OFFLINE"}
        </span>
        {device.relay && (
          <span className="ipwd-rp-relay">Relé: {device.relay.toUpperCase()}</span>
        )}
      </div>

      {/* 3 cards — Score / RSSI / Heartbeat (guia visual NOC) */}
      <div className="ipwd-rp-cards">
        <div className="ipwd-rp-card">
          <div className="ipwd-rp-card-lbl">Score</div>
          <div className="ipwd-rp-card-val" style={{ color: scoreColor(score) }}>
            {score != null ? score : "—"}
          </div>
        </div>
        <div className="ipwd-rp-card">
          <div className="ipwd-rp-card-lbl">RSSI</div>
          <div
            className="ipwd-rp-card-val"
            style={{ color: device.weak_signal ? "#FFB224" : "#d6e0f0" }}
          >
            {device.rssi != null ? `${device.rssi}` : "—"}
            {device.rssi != null && <span className="ipwd-rp-card-unit"> dBm</span>}
          </div>
        </div>
        <div className="ipwd-rp-card">
          <div className="ipwd-rp-card-lbl">Heartbeat</div>
          <div className="ipwd-rp-card-val is-sm">{fmtAgo(device.last_seen)}</div>
        </div>
      </div>

      <Row label="Último contato" value={fmtDateTime(device.last_seen)} />

      {/* Diagnóstico — principais motivos de saúde (read-only) */}
      <DeviceDiagnostics deviceId={device.device_id} max={5} />

      {/* 2. Cliente / local */}
      <Section title="Cliente / Local">
        <Row label="Cliente" value={val(device.customer_name)} />
        <Row label="Código" value={val(device.customer_code)} mono />
        {val(device.group_name) && (
          <Row label="Grupo" value={device.group_name} />
        )}
        {Array.isArray(device.tags) && device.tags.length > 0 && (
          <div className="ipwd-rp-tags">
            {device.tags.map((t) => (
              <span key={t.id || t.name} className="ipwd-rp-tag">
                {t.name || String(t)}
              </span>
            ))}
          </div>
        )}
        <Row label="Local" value={val(device.location_name)} />
        <Row label="Endereço" value={val(device.address)} />
        <Row label="Coordenadas" value={coords} mono />
      </Section>

      {/* 3. Relé */}
      <Section title="Relé">
        <Row
          label="Estado"
          value={val(device.relay) ? device.relay.toUpperCase() : null}
          highlight={relayOff ? "#FF8A3C" : online ? "#35E8A4" : null}
        />
        {relayOff && (
          <div className="ipwd-rp-warn">⚠ Relé DESLIGADO</div>
        )}
      </Section>

      {/* 4. Watchdog runtime */}
      <Section title="Watchdog">
        {snapPending ? (
          <div className="ipwd-rp-loading">carregando…</div>
        ) : (
          <>
            <Row
              label="Ativo"
              value={
                wd?.enabled === true ? "Sim"
                : wd?.enabled === false ? "Não"
                : null
              }
            />
            <Row label="Modo" value={wdMode} mono />
            <Row label="Ação" value={val(wd?.action)} mono />
            <Row
              label="Intervalo"
              value={wd?.interval != null ? `${wd.interval}s` : null}
            />
            <Row
              label="Timeout"
              value={wd?.timeout != null ? `${wd.timeout}s` : null}
            />
            <Row label="Host 1" value={wdHost1} mono />
            {wdHost2 && <Row label="Host 2" value={wdHost2} mono />}
          </>
        )}
      </Section>

      {/* 5. Agenda real (ATIVA, resolvida do snapshot — S2.9) */}
      <Section title={
        schedEntries.length > 0
          ? (<>Agenda real (Sonoff){" "}
              <span className="ipwd-exec-source">Execução: {schedSource}</span></>)
          : "Agenda real (Sonoff)"
      }>
        {snapPending ? (
          <div className="ipwd-rp-loading">carregando…</div>
        ) : schedEntries.length === 0 ? (
          activeSched.liveness?.active ? (
            <>
              {/* Stopgap — snapshot atrasado: heartbeat já indica agenda ativa. */}
              <div className="ipwd-rp-na">
                🗓 Agenda ativa (heartbeat) — aguardando snapshot da lista de eventos
              </div>
              {activeSched.liveness.nextRun > 0 && (
                <Row label="Próxima" value={fmtScheduleEpoch(activeSched.liveness.nextRun)} />
              )}
              {activeSched.liveness.lastRun > 0 && (
                <Row label="Última" value={fmtScheduleEpoch(activeSched.liveness.lastRun)} />
              )}
              {snapSyncedAt && (
                <Row label="Snapshot" value={`há ${Math.max(0, Math.round((Date.now() - new Date(snapSyncedAt).getTime()) / 60000))} min`} />
              )}
            </>
          ) : (
            <div className="ipwd-rp-na">não disponível</div>
          )
        ) : (
          <>
            <Row
              label="Ativa"
              value={activeSched.active.enabled === true ? "Sim" : "Não"}
            />
            {schedEntries.map((e, i) => (
              <div key={i} className="ipwd-rp-sched">
                <span className="ipwd-rp-sched-time">{e.time || "--:--"}</span>
                <span className="ipwd-rp-sched-days">
                  {(e.days || []).map((d) => _DOW[d]).join(", ") || "—"}
                </span>
                <span className="ipwd-rp-sched-dur">
                  {e.duration_s != null ? `${e.duration_s}s` : "—"}
                </span>
                <span className="ipwd-rp-sched-act">
                  {e.action || "relay_reboot"}
                </span>
              </div>
            ))}
          </>
        )}
      </Section>

      {/* S2.6 — Agenda V2 persistida (DORMENTE). Só aparece se houver entries. */}
      <ScheduleV2Card snapshot={snap} />

      {/* 6. Telemetria / Firmware / OTA — MAP-UX-1C: enriquece com alvo de
          release (fw fora do alvo → âmbar), OTA pendente, último OTA e
          scheduler. Todos render-puro do DTO (MAP-DATA-1A); default-safe. */}
      <Section title="Telemetria">
        <Row
          label="RSSI"
          value={device.rssi != null ? `${device.rssi} dBm` : null}
          highlight={device.weak_signal ? "#FFB224" : null}
        />
        <Row
          label="Firmware"
          value={val(device.fw_version)}
          mono
          highlight={fwStale ? "#FFB224" : null}
        />
        {fwStale && (
          <Row label="Alvo (release)" value={val(device.target_release_version)} mono highlight="#FFB224" />
        )}
        <Row label="OTA" value={val(device.ota_state)} mono />
        {device.has_pending_ota && (
          <Row label="OTA pendente" value="sim" highlight="#FFB224" />
        )}
        <Row label="Último OTA" value={val(device.last_ota_version)} mono />
        {device.scheduler_version != null && (
          <Row label="Scheduler" value={`v${device.scheduler_version}`} />
        )}
        {snapMissing && (
          <div className="ipwd-rp-hint">
            Detalhes de config indisponíveis (sem snapshot recente).
          </div>
        )}
      </Section>

      {/* MAP-HEALTH-1B — Saúde recente: janela REAL de 24h + histórico de boot.
          INFORMATIVO, não-alarmista: reboots/watchdog em 0 ficam neutros (sem
          destaque); só realça em âmbar quando > 0. Boot total é histórico
          (nunca alerta). "Último boot" é aproximado (now − uptime_s). */}
      <Section title="Saúde recente">
        <Row
          label="Reboots (24h)"
          value={val(device.reboot_count_24h)}
          highlight={device.reboot_count_24h > 0 ? "#FFB224" : null}
        />
        <Row
          label="Watchdog (24h)"
          value={val(device.watchdog_count_24h)}
          highlight={device.watchdog_count_24h > 0 ? "#FFB224" : null}
        />
        <Row label="Boot count total" value={val(device.boot_count)} mono />
        <Row label="Último boot (aprox.)" value={lastBootAprox} />
      </Section>

      {/* MAP-UX-1C — Segurança (HMAC / auth secret / MAC anchor / clone).
          Render-puro do DTO; só realça o que merece atenção (ok/0 = neutro). */}
      {hasSecurityInfo && (
        <Section title="Segurança">
          {device.secure_channel != null && (
            <Row
              label="HMAC"
              value={device.secure_channel ? "seguro" : "inseguro"}
              highlight={device.secure_channel ? null : (hmacInsecure ? "#f87171" : "#FFB224")}
            />
          )}
          {device.auth_secret_set === false && (
            <Row label="Auth secret" value="ausente" highlight="#FFB224" />
          )}
          <Row label="MAC" value={val(device.mac)} mono />
          {device.mac_anchor_status != null && (
            <Row
              label="Âncora MAC"
              value={val(device.mac_anchor_status)}
              highlight={anchorBad ? "#FFB224" : null}
            />
          )}
          {cloneSuspect && (
            <Row label="Clone" value="suspeito" highlight="#f87171" />
          )}
        </Section>
      )}

      {/* MAP-UX-1C — Proteção de produção (denylist OTA) + autorização. Roxo =
          protegido (inequívoco, casa com o anel/badge do marcador); motivo
          explícito p/ o operador entender por que ações ficam bloqueadas. */}
      <Section title="Proteção">
        <Row
          label="Produção protegida"
          value={protectedDev ? "sim · denylist OTA" : "não"}
          highlight={protectedDev ? "#A78BFA" : null}
        />
        {device.authorization_status != null && (
          <Row
            label="Autorização"
            value={val(device.authorization_status)}
            highlight={authPending ? "#FFB224" : null}
          />
        )}
      </Section>

      {/* INA226-2 — Energia (medição INA226). SÓ aparece p/ modelo compatível
          (power_monitor_supported) ou quando há dados INA226 no heartbeat;
          device comum não renderiza nada (sem bloco vazio). Erro/ausente em
          vermelho/âmbar; ok em verde. */}
      {showPower && (
        <Section title="Energia">
          <Row label="Tensão"   value={fmtV(device.bus_voltage_mv)}
               highlight={device.bus_voltage_mv != null ? "#35E8A4" : null} />
          <Row label="Corrente" value={fmtA(device.current_ma)}
               highlight={device.current_ma != null ? "#35E8A4" : null} />
          <Row label="Potência" value={fmtW(device.power_mw)}
               highlight={device.power_mw != null ? "#2BD9F0" : null} />
          <Row label="Sensor" value={powerStatusLabel} highlight={powerStatusColor} />
          {device.ina226_address != null && (
            <Row label="Endereço I2C" mono
                 value={`0x${Number(device.ina226_address).toString(16).toUpperCase()}`} />
          )}
          {device.shunt_voltage_uv != null && (
            <Row label="Shunt" value={`${device.shunt_voltage_uv} µV`} mono />
          )}
          <Row label="Última leitura" value={fmtAgo(device.last_seen)} />
        </Section>
      )}

      {/* Comandos do device — reusa handlePopupAction do MapView (mesmos
          handlers seguros do antigo popup). */}
      <DeviceCommands device={device} onCommand={onCommand} />

      {/* Navegação (sem ações destrutivas) */}
      <div className="ipwd-rp-actions">
        <button
          type="button"
          className="ipwd-rp-navbtn"
          onClick={() => onOpenDevice?.(device.device_id, "automacao")}
        >
          Abrir Automação
        </button>
        <button
          type="button"
          className="ipwd-rp-navbtn is-primary"
          onClick={() => onOpenDevice?.(device.device_id, "cockpit")}
        >
          Abrir Detalhes
        </button>
      </div>
    </aside>
  );
}
